import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { RadioButton } from '@domgen/base-design-system-2.0/atoms/RadioButton';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { FC, useEffect, useState } from 'react';
import { ArrowLeft } from '../../../assets/icons/ArrowLeft';
import { ShieldTickIcon } from '../../../assets/icons/ShieldTick';
import { StepKey } from '../../../enums/stepKey';
import { replacementCareOptionContent } from '../constants/replacementCareOptionContent';
import { useQuote } from '../../../store/selectors/useQuote';
import { QuotePageTermTypes } from './QuotePageReplacementCare.types';
import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import posthog from 'posthog-js';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { store } from '../../../store/store';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { getSystemCode } from '../../../utils/getSystemCode';
import { FormKey } from '../../../enums/formKey';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { useAppState } from '../../../store/selectors/useAppState';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { StoreEvent } from '../../../enums/storeEvent';
import { isReplacementCare } from '../../../utils/store/isReplacementCare';
import { QuoteTermsAndConditionsPanel } from '../components/QuoteTermsAndConditionsPanel';
import { useNavigate } from 'react-router-dom';
import { formStepRoutes } from '../../../routes/routes';
import { useStoreContext } from '../../../store/selectors/useStoreContext';

const styles = {
  container: 'flex-1 flex flex-col items-center desktop:px-30 py-space-500',
  panel: 'max-w-300 p-space-500 gap-space-600 rounded-radius-100 bg-neutral-0',
  heading: 'text-text-headline mb-space-200',
  subheading: 'text-body mb-space-500',
  coverOptionsHeading: 'text-h2 mb-space-400',
  coverOptionsSection: 'flex flex-col gap-space-400',
  replacementCareCard:
    'border rounded-radius-400 p-space-500 cursor-pointer transition-colors border-brand-primary bg-primary-50',
  replacementCareCardHeader:
    'flex items-start justify-between flex-row flex-wrap tablet:flex-row-reverse desktop:flex-row-reverse gap-space-300 border-b border-brand-primary mb-space-500',
  replacementCareCardTitleRow: 'flex items-start gap-space-300',
  replacementCareCardText: 'text-text-headline',
  replacementCareCardSubtitle: 'text-body text-text-secondary-500 mb-space-500',
  badge:
    'inline-flex items-center gap-space-200 rounded-radius-100 bg-success-600 text-neutral-0 text-body-small-strong px-space-400 py-space-100',
  benefitsList: 'flex flex-col gap-space-300 list-none',
  benefitDescription: 'text-body-small',
  benefitTitle: 'text-body-large-strong',
  paymentNote: 'text-body-small text-text-secondary-500 mt-space-400',
  manufacturerCard:
    'border rounded-radius-400 p-space-500 cursor-pointer transition-colors border-card-border-neutral',
  manufacturerCardContent: 'flex items-start gap-space-300',
  manufacturerCardTitle: 'text-text-headline',
  manufacturerCardSubtitle: 'text-body-small text-text-secondary-500 mt-space-200',
  termsContainer: 'mt-space-500 bg-primary-50 p-space-400',
  errorText: 'text-body-small text-red-600 mt-space-400',
  actionRow:
    'flex flex-col w-full mt-space-00 items-center md:flex-row-reverse md:justify-between mt-space-500',
  buttonWrapper: 'w-full md:w-fit mb-4',
};

export const QuoteReplacementCare: FC<{ brand: string }> = ({ brand }) => {
  const [selectedOption, setSelectedOption] = useState<QuotePageTermTypes | null>(null);
  const { VITE_CLIENT_FRIENDLY_NAME } = import.meta.env;
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const quote = useQuote();
  const quoteData = quote?.data;

  const netTotalAmount =
    quoteData?.offer.contracts[0].contractAttributes.fixed_term_single_payment?.ach.netTotalAmount;
  const gaEventData = useGaEventData() || {};
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const appSessionID = session?.id;
  const storeContext = useStoreContext();
  const hasReplacementCare = isReplacementCare(storeContext);
  const navigate = useNavigate();

  if (!quoteData) {
    store.send({ type: StoreEvent.QUOTE_ERROR });
    return null;
  }

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID,
      pagename: 'olr-protection-details-replacement-care',
      offerPrice,
      quoteId: quoteData.offer.offerReference,
    });
  }, []);

  const handleContinue = () => {
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      protectionType:
        selectedOption === 'replacement-care'
          ? 'Hisense Replacement Care'
          : "Manufacturer's warranty",
      eventAction: 'protectionDetails',
      eventLabel: 'Continue with this selection',
      pagename: 'olr-protection-details-replacement-care',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      quoteId: quoteData.offer.offerReference,
    });

    capturePostHogEvent(
      posthog,
      `protection-details-page-${selectedOption}-selected`,
      storeContext,
    );

    if (selectedOption === 'replacement-care') {
      gaECommerceEvent({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'USD',
          items: [
            {
              item_brand: brand,
              item_category: applianceCategory,
              item_appliance_type: applianceName,
              item_appliance_purchase_cost: Number(
                formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
              ),
              item_appliance_purchase_date: formatLocalisedDate(
                parseLocalDateString(
                  formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || '',
                ),
                'en-US',
              ),
              item_appliance_warranty: true,
              item_name: `${brand} ${applianceName}`,
              price: offerPrice,
              quantity: 1,
              quoteId: quoteData.offer.offerReference,
            },
          ],
        },
        pagename: 'olr-protection-details-replacement-care',
        regSystemCode: systemCode,
        version: 'WiB',
        appSessionID,
        quoteId: quoteData.offer.offerReference,
      });

      store.send({
        type: StoreEvent.CONTINUE_TO_CHECKOUT,
        data: { continueToCheckoutFromModal: false },
      });
    } else {
      store.send({ type: StoreEvent.REG_ONLY });
    }
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(formStepRoutes[formStepRoutes.length - 1].path);
  };

  return (
    <div className={styles.container}>
      <div className={styles.panel}>
        <h1 className={styles.heading}>
          Stay covered with {VITE_CLIENT_FRIENDLY_NAME} Replacement Care
        </h1>
        <p className={styles.subheading}>
          Get a replacement if it breaks down after your manufacturer's warranty ends.
        </p>

        <h2 className={styles.coverOptionsHeading}>Your cover options</h2>
        <section className={styles.coverOptionsSection}>
          <article
            className={styles.manufacturerCard}
            onClick={() => setSelectedOption('manufacturer-warranty')}
          >
            <div className={styles.manufacturerCardContent}>
              <RadioButton
                checked={selectedOption === 'manufacturer-warranty'}
                onChange={() => setSelectedOption('manufacturer-warranty')}
                name="cover-option"
                value="manufacturer-warranty"
                aria-label="Select Manufacturer Warranty"
              />
              <div>
                <h2 className={styles.manufacturerCardTitle}>Manufacturer's Warranty Only</h2>
                <p className={styles.manufacturerCardSubtitle}>
                  Manufacturer's warranty terms and conditions may vary depending on the appliance.
                  Please check with the retailer you bought your product from for more information.
                </p>
              </div>
            </div>
          </article>
          <article
            className={styles.replacementCareCard}
            onClick={() => setSelectedOption('replacement-care')}
          >
            <div className={styles.replacementCareCardHeader}>
              <span className={styles.badge}>
                <ShieldTickIcon />
                <p>Extra Protection</p>
              </span>

              <div>
                <div className={styles.replacementCareCardTitleRow}>
                  <RadioButton
                    checked={selectedOption === 'replacement-care'}
                    onChange={() => setSelectedOption('replacement-care')}
                    name="cover-option"
                    value="replacement-care"
                    aria-label="Select Hisense Replacement Care"
                  />
                  <h2 className={styles.replacementCareCardText}>
                    {VITE_CLIENT_FRIENDLY_NAME} Replacement Care
                  </h2>
                </div>

                <h2 className={`md:mt-space-200 ${styles.replacementCareCardText}`}>
                  *Add two-year protection for ${netTotalAmount}
                </h2>
                <p className={styles.replacementCareCardSubtitle}>
                  Coverage begins after the manufacturer's warranty ends
                </p>
              </div>
            </div>

            <ul className={styles.benefitsList}>
              {replacementCareOptionContent.map((benefit, index) => (
                <li
                  key={index}
                  className="first:bg-bg-primary-emphasis first:rounded-radius-200 first:p-space-200"
                >
                  <div className="flex flex-row gap-space-400 items-start first:flex ">
                    <figure className="text-icon-list-decorative shrink-0 [&>svg]:w-6 [&>svg]:h-6">
                      <benefit.icon />
                    </figure>
                    <div className="flex flex-col gap-space-100 ">
                      <p className={styles.benefitTitle}>{benefit.title}</p>
                      <p className={styles.benefitDescription}>{benefit.description}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <p className={styles.paymentNote}>
              One off payment, applicable taxes calculated during checkout.
            </p>
          </article>
        </section>

        <article className={styles.termsContainer}>
          <QuoteTermsAndConditionsPanel
            brand={`${VITE_CLIENT_FRIENDLY_NAME} Replacement Care`}
            hasReplacementCare={hasReplacementCare}
          />
        </article>

        <div className={styles.actionRow}>
          <div className={styles.buttonWrapper}>
            <Button onClick={handleContinue} disabled={!selectedOption}>
              {selectedOption ? 'Continue with this selection' : 'Select an option to continue'}
            </Button>
          </div>
          <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
            Go back
          </TextLink>
        </div>
      </div>
    </div>
  );
};
